import { useState, useCallback } from 'react';
import { useRecoilValue } from 'recoil';
import { useCookies } from 'react-cookie';
import { DOM } from '@app/utils';
import { isFirstVisitState } from '@app/page';

const isPerfectSquare = (num): Boolean => Number.isInteger(Math.sqrt(num));

const calcInitialValue = (
  curCookie,
  { isFirstVisit, firstVisitOnly, enabled, resetPeriod },
) => {
  if (!enabled) {
    return false;
  }

  if (!curCookie) {
    return !firstVisitOnly || isFirstVisit;
  }

  const [key, count] = curCookie.split('-');
  return key !== resetPeriod || (count !== 'DONE' && isPerfectSquare(+count));
};

/**
 * Hook for executing a block with an exponential chronological events Ex: On nth action where n is 1,2,4,9,16...
 * @param callback
 * @param param1
 * @returns
 */
const useExponentialChronology = (
  callback,
  {
    enabled = true,
    firstVisitOnly = true,
    key,
    resetPeriod = 'uEC',
    resetOnNewSession = false,
    bufferNum = 5,
  },
) => {
  const isFirstVisit = useRecoilValue(isFirstVisitState);
  const [cookies] = useCookies([`${key}`]);

  const [active, setActive] = useState(
    calcInitialValue(cookies[key], {
      isFirstVisit,
      firstVisitOnly,
      enabled,
      resetPeriod,
    }),
  );

  const updateCookie = useCallback(
    (count = 0) => {
      DOM.forceSetCookie(
        key,
        `${resetPeriod}-${+count + 1}`,
        !resetOnNewSession
          ? {
              path: '/',
              maxAge: 3.154e7,
            }
          : undefined,
      );
    },
    [key, resetOnNewSession, resetPeriod],
  );

  const stop = useCallback(() => {
    DOM.forceSetCookie(
      key,
      `${resetPeriod}-DONE`,
      !resetOnNewSession
        ? {
            path: '/',
            maxAge: 3.154e7,
          }
        : undefined,
    );
  }, [key, resetOnNewSession, resetPeriod]);

  const increment = () => {
    if (!enabled) {
      return;
    }

    setActive(false);
    const cookie: string = DOM.getCookie(key) ?? '';

    if (!cookie) {
      if (!firstVisitOnly || isFirstVisit) {
        updateCookie(0);
        callback();
        setActive(true);
      }
    } else {
      const [key, count] = cookie.split('-');
      // Period changed, so resetting
      if (key !== resetPeriod) {
        updateCookie(0);
        callback();
        setActive(true);
      } else if (count !== 'DONE') {
        if ((bufferNum && +count < bufferNum) || isPerfectSquare(+count)) {
          callback();
          setActive(true);
        }

        updateCookie(+count);
      }
    }
  };

  return {
    active,
    increment,
    stop,
  };
};

export default useExponentialChronology;
